import React, { useMemo, useState } from "react";
import { PhoneInput, defaultCountries, parseCountry } from "react-international-phone";
import "react-international-phone/style.css";
import { isPossiblePhoneNumber, isValidPhoneNumber } from "libphonenumber-js/min";
import { detectCountry, PREFERRED } from "../utils/country";

/* Phone field that sorts out the country for you (same as the website's sign-up form):
   - starts on the country this device is in
   - typing a country code (+255..., or just 255...) switches to that country
   - a leading 0 (0712...) or a repeated country code is dropped, so the value is always clean international form (+254712345678) */
const FLAGS = defaultCountries.map((c) => { const { iso2 } = parseCountry(c); return { iso2, src: `https://cdn.jsdelivr.net/npm/flag-icons@7.2.3/flags/4x3/${iso2}.svg` }; });
const dialOf = (iso2) => parseCountry(defaultCountries.find((c) => parseCountry(c).iso2 === iso2)).dialCode;

export const isUsablePhone = (value) => isPossiblePhoneNumber(value);
/** Numbers are stored as digits with the country code (254712345678); the field wants +254712345678. */
export const toPlus = (p) => (p && /^\d{8,15}$/.test(String(p)) ? "+" + p : p || "");

export function PhoneField({ label, value, onChange, error, autoFocus, bare = false, hint }) {
  const [country, setCountry] = useState(() => detectCountry());
  const initial = useMemo(() => country, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tidy = (phone, iso2) => {
    const dial = dialOf(iso2);
    const digits = phone.replace(/\D/g, "");
    if (digits.startsWith("0")) return "+" + dial + digits.replace(/^0+/, "");
    if (!digits.startsWith(dial)) return phone;
    let national = digits.slice(dial.length);
    // another country's code typed without the plus: "255712345678" on a Kenyan field -> switch to Tanzania
    if (national.length >= 8 && !isValidPhoneNumber("+" + dial + national) && isValidPhoneNumber("+" + national)) return "+" + national;
    // the same country code typed twice: "+254254712345678"
    if (national.startsWith(dial) && isPossiblePhoneNumber("+" + dial + national.slice(dial.length)) && !isPossiblePhoneNumber("+" + dial + national)) national = national.slice(dial.length);
    // a leading 0: "+2540712345678" -> "+254712345678"
    return "+" + dial + national.replace(/^0+/, "");
  };

  return (
    <div>
      <div className="relative">
        <PhoneInput
          defaultCountry={initial} value={value} preferredCountries={PREFERRED} flags={FLAGS} autoFocus={autoFocus}
          onChange={(phone, meta) => { setCountry(meta.country.iso2); onChange(tidy(phone, meta.country.iso2)); }}
          inputProps={{
            autoComplete: "tel", inputMode: "tel", "aria-label": label, "aria-invalid": error ? true : undefined,
            // pasting a local number such as "0712 345 678": read it as a number of the selected country
            onPaste: (e) => {
              const text = e.clipboardData.getData("text").trim();
              const digits = text.replace(/\D/g, "");
              if (text.startsWith("+") || text.startsWith("00") || !digits.startsWith("0")) return;
              e.preventDefault();
              onChange("+" + dialOf(country) + digits.replace(/^0+/, ""));
            },
          }}
          className={"phone-field " + (error ? "phone-field--error" : "")} inputClassName="phone-field__input"
          style={bare ? { "--react-international-phone-background-color": "var(--input-bg, #fff)", "--react-international-phone-text-color": "var(--text-color, #202124)", "--react-international-phone-border-color": "var(--input-border, #d1d5db)", "--react-international-phone-height": "42px", "--react-international-phone-border-radius": "6px", "--react-international-phone-font-size": "14px", width: "100%" } : undefined}
        />
        {!bare && <span className="pointer-events-none absolute left-3 top-0 -translate-y-1/2 bg-[#ffffff] px-1 text-xs text-[#5F6368]">{label}</span>}
      </div>
      {(!bare || hint) && (
        <p className={bare ? "mt-1 text-xs text-gray-500" : "mt-1.5 px-4 text-xs text-[#5F6368]"}>
          {hint || (country !== "ke" ? "Outside Kenya your sign-in code is sent on WhatsApp." : "Type your number, or start with + and a country code.")}
        </p>
      )}
    </div>
  );
}
